import { createFileRoute, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { StatCard } from '@/components/ui/stat-card';
import { DataTable, type Column } from '@/components/data-table/data-table';
import { Pagination } from '@/components/data-table/pagination';
import { queryKeys } from '@/lib/query-keys';
import { formatRelative } from '@/lib/format';
import {
  listModerationQueue,
  getModerationStats,
  type Review,
  type ModerationQueueQuery,
} from '@/features/moderation/api';
import { ResolveDialog } from '@/features/moderation/components/resolve-dialog';
import { ModerationEnforceDialog } from '@/features/moderation/components/enforce-dialog';

const PAGE_SIZE = 20;

const STATUS_FILTER = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In review' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: '', label: 'All' },
];

interface ModerationSearch {
  status?: string;
  page?: number;
}

export const Route = createFileRoute('/_authenticated/moderation/')({
  validateSearch: (search: Record<string, unknown>): ModerationSearch => ({
    status: typeof search['status'] === 'string' ? search['status'] : undefined,
    page: typeof search['page'] === 'number' ? search['page'] : undefined,
  }),
  component: ModerationPage,
});

function ModerationPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/moderation/' });
  const status = search.status ?? 'pending';
  const page = search.page ?? 1;
  const offset = (page - 1) * PAGE_SIZE;

  const [resolveTarget, setResolveTarget] = useState<Review | null>(null);
  const [enforceUserId, setEnforceUserId] = useState<string | null>(null);

  const statsQuery = useQuery({
    queryKey: queryKeys.moderation.stats(),
    queryFn: getModerationStats,
  });

  const filters: ModerationQueueQuery = {
    review_status: (status || undefined) as ModerationQueueQuery['review_status'],
    limit: PAGE_SIZE,
    offset,
  };

  const query = useQuery({
    queryKey: queryKeys.moderation.queue({ status, page }),
    queryFn: () => listModerationQueue(filters),
    placeholderData: (prev) => prev,
  });

  function patchSearch(patch: Partial<ModerationSearch>) {
    void navigate({ to: '/moderation', search: (prev) => ({ ...prev, ...patch }) });
  }

  const stats = statsQuery.data;

  const columns: Column<Review>[] = [
    {
      key: 'target',
      header: 'Target user',
      render: (r) => (
        <Link
          to="/users/$userId"
          params={{ userId: r.target_user_id }}
          className="wb-mono wb-small"
          onClick={(e) => e.stopPropagation()}
        >
          {r.target_user_id.slice(0, 12)}…
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge>,
    },
    {
      key: 'score',
      header: 'Score',
      align: 'right',
      render: (r) => <span className="wb-mono">{(r.trigger_score ?? 0).toFixed(1)}</span>,
    },
    {
      key: 'created',
      header: 'Created',
      align: 'right',
      render: (r) => <span className="wb-small">{formatRelative(r.created_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="secondary" onClick={() => setResolveTarget(r)}>
            Resolve
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEnforceUserId(r.target_user_id)}>
            Enforce
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="flex flex-col gap-1.5" style={{ marginBottom: 20 }}>
        <span className="wb-eyebrow">Operations</span>
        <h1 className="wb-h1">Moderation</h1>
        <p className="wb-sub">Review flagged accounts and enforce policy.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5" style={{ marginBottom: 20 }}>
        <StatCard label="Pending" value={num(stats?.pending_reviews)} />
        <StatCard label="Watched" value={num(stats?.watched_users)} />
        <StatCard label="Restricted" value={num(stats?.restricted_users)} />
        <StatCard label="Suspended" value={num(stats?.suspended_users)} />
        <StatCard label="Banned" value={num(stats?.banned_users)} />
      </div>

      <div className="wb-toolbar" style={{ marginBottom: 16 }}>
        <Select
          size="sm"
          options={STATUS_FILTER}
          value={status}
          onChange={(e) => patchSearch({ status: e.target.value || undefined, page: 1 })}
          style={{ width: 160 }}
        />
      </div>

      <DataTable
        columns={columns}
        rows={query.data?.reviews}
        rowKey={(r) => r.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => query.refetch()}
        empty={{ title: 'Queue is clear', hint: 'No reviews match this filter.' }}
      />

      {query.data && query.data.total > 0 ? (
        <Pagination
          page={page}
          limit={PAGE_SIZE}
          total={query.data.total}
          onPageChange={(p) => patchSearch({ page: p })}
        />
      ) : null}

      <ResolveDialog
        open={resolveTarget !== null}
        onClose={() => setResolveTarget(null)}
        review={resolveTarget}
      />
      <ModerationEnforceDialog
        open={enforceUserId !== null}
        onClose={() => setEnforceUserId(null)}
        userId={enforceUserId}
      />
    </div>
  );
}

function num(n: number | undefined): string {
  return n == null ? '—' : n.toLocaleString();
}

function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'in_review':
      return 'info';
    case 'resolved':
      return 'success';
    case 'dismissed':
      return 'neutral';
    default:
      return 'neutral';
  }
}
